import { ReactNode } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useGetAllPayersQuery } from "store/queries/payers";
import { RulesetApplicability } from "types/DefaultRules/DefaultRules";

import {
  ALL_PAYERS_LABEL,
  ALL_PAYERS_OPTION,
} from "../helpers/ruleApplicability";
import { RuleApplicabilityControl } from "./RuleApplicabilityControl";

export const ALL_PAYERS_MIXED_MESSAGE = `"${ALL_PAYERS_LABEL}" cannot be combined with specific payers. Remove one or the other.`;

/** True when a multi-payer selection holds "All payers" next to a named payer. */
export const mixesAllPayersWithPayers = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length > 1 &&
  value.some((payer) => payer?.value === null);

interface IRuleApplicabilityFieldProps {
  /** The form's own provider/payer pickers, shown on a Payer-Provider rule. */
  scopedFields: ReactNode;
  /** Edit pages: the applicability the rule was loaded with (see RuleApplicabilityControl). */
  savedApplicability?: RulesetApplicability;
  /** Edit pages: why converting is not offered right now. */
  conversionBlockedHint?: string;
}

/**
 * CLE-2842 step 2 — RuleApplicabilityControl bound to the MPPR / Case Rate / Package forms
 * (react-hook-form). Form fields it reads and writes:
 *   applicability   — Payer-Provider / Typical / Fallback
 *   isDefault       — kept equal to "not a Payer-Provider rule", which is what the legacy readers
 *                     of this flag (the provider/payer sections, the case-rate conflict check)
 *                     need to know
 *   fallbackPayerId — a Fallback rule's payer (null = All payers)
 *   payerName       — on the edit page, a single `{ name, value }`: a conversion carries the payer
 *                     across, so "this provider, Aetna" becomes "Aetna, every provider" and back
 */
export function RuleApplicabilityField({
  scopedFields,
  savedApplicability,
  conversionBlockedHint,
}: IRuleApplicabilityFieldProps) {
  const { control, setValue } = useFormContext<any>();
  const { data: payers } = useGetAllPayersQuery();
  const isDefault = useWatch({ control, name: "isDefault" });
  const watchedApplicability = useWatch({ control, name: "applicability" });
  const fallbackPayerId = useWatch({ control, name: "fallbackPayerId" });
  const payerName = useWatch({ control, name: "payerName" });

  const applicability: RulesetApplicability =
    watchedApplicability ?? (isDefault ? "TYPICAL" : "SCOPED");
  // Create and copy pages pick several payers; only the edit page's single payer is carried.
  const singlePayer =
    payerName && !Array.isArray(payerName) ? payerName : undefined;
  const singlePayerId: number | null | undefined =
    singlePayer && singlePayer.value !== undefined
      ? singlePayer.value
      : undefined;
  const payerId =
    applicability === "FALLBACK" ? fallbackPayerId ?? null : singlePayerId;

  const payerOption = (id: number | null) => {
    if (id === null) return ALL_PAYERS_OPTION;
    const payer = (payers?.data ?? []).find((p: any) => p.id === id);
    return { name: payer?.name ?? String(id), value: id };
  };

  return (
    <Controller
      name="applicability"
      control={control}
      render={({ field }) => (
        <RuleApplicabilityControl
          applicability={applicability}
          onApplicabilityChange={(next) => {
            if (next === "FALLBACK" && singlePayer) {
              setValue("fallbackPayerId", singlePayerId ?? null);
            }
            if (
              next === "SCOPED" &&
              applicability === "FALLBACK" &&
              singlePayer
            ) {
              setValue("payerName", payerOption(fallbackPayerId ?? null));
            }
            setValue("isDefault", next !== "SCOPED");
            field.onChange(next);
          }}
          payerId={payerId}
          onPayerChange={(id) => setValue("fallbackPayerId", id)}
          scopedFields={scopedFields}
          savedApplicability={savedApplicability}
          conversionBlockedHint={conversionBlockedHint}
        />
      )}
    />
  );
}

const hasOptionValue = (option: any) =>
  !!option && option.value !== undefined && option.value !== "";

/**
 * Whether the provider/payer part of an MPPR / Case Rate / Package form can be saved. A
 * Payer-Provider rule needs a provider and a payer — "All payers" counts, but not next to named
 * payers. Typical and Fallback rules need neither. The submit buttons read this alongside the
 * form's own validity, which does not re-check fields that a scope change mounts or unmounts.
 */
export function useRuleScopeComplete(): boolean {
  const { control } = useFormContext<any>();
  const [applicability, isDefault, provider, payerName] = useWatch({
    control,
    name: ["applicability", "isDefault", "provider", "payerName"],
  });
  const resolved: RulesetApplicability =
    applicability ?? (isDefault ? "TYPICAL" : "SCOPED");
  if (resolved !== "SCOPED") return true;
  const providerOk = Array.isArray(provider)
    ? provider.length > 0 && provider.every((p: any) => !!p?.value)
    : !!provider?.value;
  const payerOk = Array.isArray(payerName)
    ? payerName.length > 0 &&
      payerName.every(hasOptionValue) &&
      !mixesAllPayersWithPayers(payerName)
    : hasOptionValue(payerName);
  return providerOk && payerOk;
}
